import * as React from 'react'
import {ReactNode} from 'react'
import moment from 'moment/moment'
import {
    NatGuestOwnershipTransferNewMemberDetailInfo,
    NatGuestOwnershipTransferNewMemberOutlineInfo
} from './NatGuestOwnershipTransferNewMemberInterface'
import {
    APPLY_CATEGORY,
    DESTINATION_EXISTENCE,
    PAYMENT_EXISTENCE,
    RENT_EXISTENCE,
    RESIDENT_PLACE,
    ROLE_FOR_GUEST
} from '../../../../../CodeMaster'
import {
    GuestLoginInfoSessionStorageKey,
    NatGuestLoginInfoSessionStorageHelper
} from '../../../../common/authentication/NatGuestLoginInfoSessionStorageHelper'
import {convertToRoomNumberForResident, getFormattedAddress} from '../../../../common/util/Natutil'
import NatGuestOwnershipTransfersDetailConfirmItem from '../../common/NatGuestOwnershipTransfersDetailConfirmItem'
import {BlockSpacer} from '../../../../../styledComponents/ResponsiveConvenience'
import {formatDate} from '../../../../../formatters/formatDate'
import {Nullable} from '../../../../../app/TypeHelpers'
import {StyledDiv} from '../../../../../styledComponents/StyledTypeDefinitions'
import styled from 'styled-components'

interface Props {
    outlineInfo: Nullable<NatGuestOwnershipTransferNewMemberOutlineInfo>
    detailInfo: Nullable<NatGuestOwnershipTransferNewMemberDetailInfo>
    ownershipTransferDate: Nullable<string>
    accessExpirationDate?: string
    applyId?: string
    isApply?: boolean
}

const NatGuestNewMemberConfirm = ({applyId, isApply, ...otherProps}: Props) => {
    const {ownershipTransferDate, outlineInfo, detailInfo, accessExpirationDate} = otherProps

    if (!outlineInfo || !detailInfo) {
        return <></>
    }

    const ownershipTransferDateLabel = ownershipTransferDate ? formatDate(ownershipTransferDate) : ''
    const accessExpirationDateLabel = accessExpirationDate ? formatDate(accessExpirationDate) : null;
    const residentPlaceLabel =
        outlineInfo.residentPlace ? RESIDENT_PLACE.codeToEnumConstants(outlineInfo.residentPlace).label : ''
    const paymentExistenceLabel =
        detailInfo.paymentExistence ? PAYMENT_EXISTENCE.codeToEnumConstants(detailInfo.paymentExistence).label : ''
    const destinationExistenceLabel =
        detailInfo.destinationExistence ?
            DESTINATION_EXISTENCE.codeToEnumConstants(detailInfo.destinationExistence).label : ''
    const applyCategoryLabel = APPLY_CATEGORY.OWNERSHIP_TRANSFER.label + '（' + ROLE_FOR_GUEST.NEW_MEMBER.label + '様向け）'
    const applicantRoom = NatGuestLoginInfoSessionStorageHelper
            .getStringItemOrNull(GuestLoginInfoSessionStorageKey.SELECTED_CONDOMINIUM_NAME) + '　' +
        NatGuestLoginInfoSessionStorageHelper
            .getStringItemOrNull(GuestLoginInfoSessionStorageKey.SELECTED_RIDGE_NAME) + ' ' +
        convertToRoomNumberForResident(
            NatGuestLoginInfoSessionStorageHelper
                .getStringItemOrNull(GuestLoginInfoSessionStorageKey.SELECTED_ROOM_NUMBER)
        )

    const paymentChangeDeadLineInfo = (): ReactNode => {
        const momentOwnershipTransferDate = moment(ownershipTransferDate)
        const oldMemberDate = momentOwnershipTransferDate.clone().add(1, 'months') // 翌月
        const newMemberDate = momentOwnershipTransferDate.clone().add(2, 'months') // 翌々月

        const items = []
        items.push(`旧組合員：${oldMemberDate.year()}年${oldMemberDate.month() + 1}月分まで支払い`)
        items.push(`新組合員：${newMemberDate.year()}年${newMemberDate.month() + 1}月分から支払い`)

        return <>
            <BlockSpacer height={'5px'}/>
            <NatGuestOwnershipTransfersDetailConfirmItem
                title={'管理費等の支払変更期日'}
                items={items}
            />
        </>
    }

    const newMemberInfo = (): ReactNode => {
        const items = []
        const {familyName, firstName, familyNameKana, firstNameKana} = outlineInfo
        const newMemberName = familyName + ' ' + firstName + '（' + familyNameKana + ' ' + firstNameKana + ')'
        items.push(`氏名：${newMemberName}`)
        items.push(`メールアドレス：${outlineInfo.mailAddress}`)

        return <>
            <BlockSpacer height={'5px'}/>
            <NatGuestOwnershipTransfersDetailConfirmItem
                title={'新組合員情報'}
                items={items}
            />
        </>
    }

    const sharerMemberInfo = (): ReactNode => {
        if (outlineInfo.sharer.length === 0) {
            return <></>
        }

        const items = []
        const {sharer} = outlineInfo
        for (let i = 0; i < sharer.length; i++) {
            items.push('共有者' + [i + 1] + '：'
                + sharer[i].sharerFamilyName + ' ' + sharer[i].sharerFirstName
                + '（' + sharer[i].sharerFamilyNameKana + ' ' + sharer[i].sharerFirstNameKana + ')')
        }

        return <>
            <BlockSpacer height={'5px'}/>
            <NatGuestOwnershipTransfersDetailConfirmItem
                title={'共有者情報'}
                items={items}
            />
        </>
    }

    const moveInDateInfo = (): ReactNode => {
        if (detailInfo.moveInDate === null) {
            return <></>
        }

        return <>
            <BlockSpacer height={'5px'}/>
            <NatGuestOwnershipTransfersDetailConfirmItem
                title={'入居予定日'}
                items={[`${formatDate(detailInfo.moveInDate)}`]}
            />
        </>
    }

    const beforeContactAddressInfo = (): ReactNode => {
        if (outlineInfo.residentPlace !== RESIDENT_PLACE.RESIDING.value || detailInfo.moveInDate === null) {
            return <></>
        }

        const {moveInDate, beforeContactAddress} = detailInfo
        const needBeforeContactAddress =
            (moveInDate >= moment(ownershipTransferDate).clone().add(60, 'days').format('YYYY-MM-DD'))
        if (!needBeforeContactAddress || !beforeContactAddress) {
            return <></>
        }

        const items = []
        items.push(`電話番号：${beforeContactAddress.beforeContactPhoneNumber}`)
        items.push(`住所：${getFormattedAddress(
            beforeContactAddress.beforeContactZipCode ?? '',
            beforeContactAddress.beforeContactPrefecture ?? '',
            beforeContactAddress.beforeContactAddress2 ?? '',
            beforeContactAddress.beforeContactAddress3 ?? '',
            beforeContactAddress.beforeContactBuildingRoomNumber ?? '')}`)

        return <>
            <BlockSpacer height={'5px'}/>
            <NatGuestOwnershipTransfersDetailConfirmItem
                title={'入居までの連絡先'}
                items={items}
            />
        </>
    }

    const contactInfo = (): ReactNode => {
        if (!detailInfo.contactInformation) {
            return <></>
        }

        const items = []
        const {contactInformation} = detailInfo
        let contactInformationLabel = ''

        if (outlineInfo.residentPlace === RESIDENT_PLACE.RESIDING.value ||
            outlineInfo.residentPlace === RESIDENT_PLACE.DOMESTIC.value) {
            contactInformationLabel = outlineInfo.residentPlace === RESIDENT_PLACE.RESIDING.value ? '入居後の連絡先' : '連絡先'
            items.push(`電話番号：${contactInformation.contactPhoneNumber}`)
            items.push(`住所：${getFormattedAddress(
                contactInformation.contactZipCode ?? '',
                contactInformation.contactPrefecture ?? '',
                contactInformation.contactAddress2 ?? '',
                contactInformation.contactAddress3 ?? '',
                contactInformation.contactBuildingRoomNumber ?? '')}`)
        } else if (outlineInfo.residentPlace === RESIDENT_PLACE.FOREIGN.value) {
            contactInformationLabel = '連絡先'
            items.push(`電話番号：${contactInformation.contactPhoneNumber}`)
            items.push(`住所：${contactInformation.contactAddress ?? ''}`)
        } else {
            return <></>
        }

        return <>
            <BlockSpacer height={'5px'}/>
            <NatGuestOwnershipTransfersDetailConfirmItem
                title={contactInformationLabel}
                items={items}
            />
        </>
    }

    const proxyContactAddressInfo = (): ReactNode => {
        if (!outlineInfo || !detailInfo ||
            outlineInfo.residentPlace !== RESIDENT_PLACE.FOREIGN.value) {
            return <></>
        }

        const {proxyContactAddress} = detailInfo
        if (!proxyContactAddress) {
            return <></>
        }

        const items = []
        items.push(`組合員との関係性：${proxyContactAddress.proxyContactRelationship}`)
        items.push(
            `氏名：${proxyContactAddress.proxyContactFamilyName + ' ' + proxyContactAddress.proxyContactFirstName}`)
        items.push(`電話番号：${proxyContactAddress.proxyContactPhoneNumber}`)
        items.push(`住所：${getFormattedAddress(
            proxyContactAddress.proxyContactZipCode ?? '',
            proxyContactAddress.proxyContactPrefecture ?? '',
            proxyContactAddress.proxyContactAddress2 ?? '',
            proxyContactAddress.proxyContactAddress3 ?? '',
            proxyContactAddress.proxyContactBuildingRoomNumber ?? '')}`)

        return <>
            <BlockSpacer height={'5px'}/>
            <NatGuestOwnershipTransfersDetailConfirmItem
                title={'国内の代理連絡先'}
                items={items}
            />
        </>
    }


    const rentExistenceInfo = (): ReactNode => {
        if (outlineInfo.residentPlace === RESIDENT_PLACE.RESIDING.value || detailInfo.rentExistence === null) {
            return <></>
        }

        return <>
            <BlockSpacer height={'5px'}/>
            <NatGuestOwnershipTransfersDetailConfirmItem
                title={'賃貸'}
                items={[`${RENT_EXISTENCE.codeToEnumConstants(detailInfo.rentExistence).label}`]}
            />
        </>
    }

    const paymentContactAddressInfo = (): ReactNode => {
        if (detailInfo.paymentExistence === PAYMENT_EXISTENCE.PRINCIPAL.value) {
            return <></>
        }

        const {paymentContactAddress} = detailInfo
        if (!paymentContactAddress) {
            return <></>
        }

        const items = []
        items.push(`組合員との関係性：${paymentContactAddress.paymentRelationship}`)
        items.push(`氏名または法人名：${paymentContactAddress.paymentName}`)
        items.push(`電話番号：${paymentContactAddress.paymentPhoneNumber}`)
        items.push(`住所：${getFormattedAddress(
            paymentContactAddress.paymentZipCode ?? '',
            paymentContactAddress.paymentPrefecture ?? '',
            paymentContactAddress.paymentAddress2 ?? '',
            paymentContactAddress.paymentAddress3 ?? '',
            paymentContactAddress.paymentBuildingRoomNumber ?? '')}`)

        return <>
            <BlockSpacer height={'5px'}/>
            <NatGuestOwnershipTransfersDetailConfirmItem
                title={'支払者情報'}
                items={items}
            />
        </>
    }

    const destinationContactAddressInfo = (): ReactNode => {
        if (detailInfo.destinationExistence === DESTINATION_EXISTENCE.PRINCIPAL.value) {
            return <></>
        }

        const {destinationContactAddress} = detailInfo
        if (!destinationContactAddress) {
            return <></>
        }

        const items = []
        items.push(`氏名または法人名：${destinationContactAddress.destinationName}`)
        items.push(`電話番号：${destinationContactAddress.destinationPhoneNumber}`)
        items.push(`住所：${getFormattedAddress(
            destinationContactAddress.destinationZipCode ?? '',
            destinationContactAddress.destinationPrefecture ?? '',
            destinationContactAddress.destinationAddress2 ?? '',
            destinationContactAddress.destinationAddress3 ?? '',
            destinationContactAddress.destinationBuildingRoomNumber ?? '')}`)

        return <>
            <BlockSpacer height={'5px'}/>
            <NatGuestOwnershipTransfersDetailConfirmItem
                title={'送付先情報'}
                items={items}
            />
        </>
    }

    return (<>
        {applyId && !isApply &&
            <>
                {
                    accessExpirationDateLabel &&
                    <ViewExpirationDateDiv>閲覧期限：{[accessExpirationDateLabel]}</ViewExpirationDateDiv>
                }
                <NatGuestOwnershipTransfersDetailConfirmItem
                    title={'申請ID'}
                    items={[applyId]}
                />
                <BlockSpacer height={'5px'}/>
            </>
        }
        <NatGuestOwnershipTransfersDetailConfirmItem
            title={'申請区分'}
            items={[applyCategoryLabel]}
        />
        <BlockSpacer height={'5px'}/>
        <NatGuestOwnershipTransfersDetailConfirmItem
            title={'対象住戸'}
            items={[`${applicantRoom}`]}
        />
        <BlockSpacer height={'5px'}/>
        <NatGuestOwnershipTransfersDetailConfirmItem
            title={'組合員変更年月日'}
            items={[`${ownershipTransferDateLabel}`]}
        />
        {/* 管理費等の支払変更期日 */}
        {paymentChangeDeadLineInfo()}
        {/* 新組合員情報 */}
        {newMemberInfo()}
        {/* 共有者情報 */}
        {sharerMemberInfo()}
        <BlockSpacer height={'5px'}/>
        <NatGuestOwnershipTransfersDetailConfirmItem
            title={'居住先'}
            items={['この部屋に' + residentPlaceLabel]}
        />
        {/* 入居予定日 */}
        {moveInDateInfo()}
        {/* 入居前の連絡先 */}
        {beforeContactAddressInfo()}
        {/* 連絡先 */}
        {contactInfo()}
        {/* 国内の代理連絡先 */}
        {proxyContactAddressInfo()}
        {/* 賃貸 */}
        {rentExistenceInfo()}
        <BlockSpacer height={'5px'}/>
        <NatGuestOwnershipTransfersDetailConfirmItem
            title={'管理費等の支払情報'}
            items={[paymentExistenceLabel]}
        />
        {/* 支払者情報 */}
        {paymentContactAddressInfo()}
        <BlockSpacer height={'5px'}/>
        <NatGuestOwnershipTransfersDetailConfirmItem
            title={'請求書の送付先情報'}
            items={[destinationExistenceLabel]}
        />
        {/* 送付先情報 */}
        {destinationContactAddressInfo()}
    </>)
}

NatGuestNewMemberConfirm.displayName = 'NatGuestNewMemberConfirm'
export default NatGuestNewMemberConfirm

const ViewExpirationDateDiv: StyledDiv = styled.div`
  display: block;
  text-align: right;
  margin-bottom: 5px;
`
